// ================= IMPORTS =================
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  MapPin,
  Clock,
  IndianRupee,
  ArrowRight,
  Loader2,
  Building2,
  X,
  Send,
  Upload,
  BadgeCheck,
  Sparkles,
  Users,
  Trophy,
  Heart,
  Search,
  Briefcase,
} from "lucide-react";

const Career = () => {
  const [searchParams] = useSearchParams();

  const empId = searchParams.get("empId") || "13";
  const comId = searchParams.get("comId") || 1;

  // ================= STATES =================
  const [jobsData, setJobsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);

  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);

  // ================= FILTER STATES =================
  const [searchTerm, setSearchTerm] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [workModeFilter, setWorkModeFilter] = useState("");

  // ================= FORM =================
  const defaultForm = {
    applyId: 0,
    jobId: 0,
    fullName: "",
    emailAddress: "",
    mobileNumber: "",
    currentLocation: "",
    appliedPosition: "",
    totalExperience: "",
    currentCompany: "",
    currentSalary: "",
    expectedSalary: "",
    noticePeriod: "",
    skillsTechnologies: "",
    languagesKnown: "",
    resumeFile: "",
    linkedInProfile: "",
    gitHubPortfolioLink: "",
    availableJoiningDate: "",
    applicationStatus: "Pending",
    addedDate: new Date().toISOString(),
    modifiedDate: new Date().toISOString(),
    isDeleted: false,
    isActive: true,
    empId: Number(empId),
    comId: Number(comId),
    talentId: 0,
  };

  const [formData, setFormData] = useState(defaultForm);

  // ================= FETCH JOBS =================
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);

        const response = await fetch(
          "https://erpapi.sapeagleerp.com/api/JobListing/company/1"
        );

        const data = await response.json();

        setJobsData(Array.isArray(data) ? data : []);
      } catch (error) {
        console.log(error);
        setJobsData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, [comId]);

  // ================= FILTERED JOBS =================
  const filteredJobs = useMemo(() => {
    return jobsData.filter((job) => {
      const matchesSearch =
        job.jobTitle
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        job.jobDescription
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase());

      const matchesLocation = locationFilter
        ? job.location
            ?.toLowerCase()
            .includes(locationFilter.toLowerCase())
        : true;

      const matchesWorkMode = workModeFilter
        ? job.workMode === workModeFilter
        : true;

      return (
        matchesSearch &&
        matchesLocation &&
        matchesWorkMode
      );
    });
  }, [
    jobsData,
    searchTerm,
    locationFilter,
    workModeFilter,
  ]);

  // ================= HANDLERS =================
  const handleApplyClick = (job) => {
    setSelectedJob(job);

    setFormData({
      ...defaultForm,
      jobId: job.jobId,
      appliedPosition: job.jobTitle,
      empId: Number(empId),
      comId: Number(comId),
    });

    setIsApplyModalOpen(true);
  };

  const handleViewDetails = (job) => {
    setSelectedJob(job);
    setIsViewModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    // ================= FILE UPLOAD =================
    if (name === "resumeFile") {
      setFormData((prev) => ({
        ...prev,
        resumeFile: files[0]
          ? files[0].name
          : "",
      }));

      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ================= SUBMIT =================
  const handleSubmitApplication = async (e) => {
    e.preventDefault();

    try {
      setApplyLoading(true);

      const payload = {
        applyId: 0,

        jobId: formData.jobId,

        fullName: formData.fullName,

        emailAddress: formData.emailAddress,

        mobileNumber: formData.mobileNumber,

        currentLocation: formData.currentLocation,

        appliedPosition: formData.appliedPosition,

        totalExperience: formData.totalExperience,

        currentCompany: formData.currentCompany,

        currentSalary: formData.currentSalary,

        expectedSalary: formData.expectedSalary,

        noticePeriod: formData.noticePeriod,

        skillsTechnologies:
          formData.skillsTechnologies,

        languagesKnown:
          formData.languagesKnown || "",

        resumeFile:
          formData.resumeFile || "",

        linkedInProfile:
          formData.linkedInProfile || "",

        gitHubPortfolioLink:
          formData.gitHubPortfolioLink || "",

        availableJoiningDate:
          formData.availableJoiningDate
            ? new Date(
                formData.availableJoiningDate
              ).toISOString()
            : new Date().toISOString(),

        applicationStatus: "Pending",

        addedDate: new Date().toISOString(),

        modifiedDate: new Date().toISOString(),

        isDeleted: false,

        isActive: true,

        empId: Number(empId),

        comId: Number(comId),

        talentId: 0,
      };

      console.log("PAYLOAD =>", payload);

      const response = await fetch(
        "https://erpapi.sapeagleerp.com/api/JobApply/create",
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json",
          },

          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      console.log("RESPONSE =>", data);

      if (response.ok) {
        alert(
          "Application Submitted Successfully 🚀"
        );

        setIsApplyModalOpen(false);

        setFormData(defaultForm);
      } else {
        alert(
          data?.message ||
            "Failed To Apply"
        );

        console.log(data);
      }
    } catch (error) {
      console.log(error);

      alert("Something Went Wrong");
    } finally {
      setApplyLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 font-sans">
      <div className="max-w-7xl mx-auto px-4 py-8">

        {/* HEADER */}
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <Sparkles size={16} /> We Are Hiring
          </div>

          <h1 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tight">
            Find Your Next Opportunity
          </h1>

          <p className="text-slate-600 mt-3 text-lg max-w-xl">
            Join a dynamic team where innovation
            meets growth.
          </p>

          {/* STATS */}
          <div className="flex flex-wrap gap-4 mt-8">
            <div className="bg-white rounded-2xl px-6 py-4 shadow-sm border border-slate-100">
              <p className="text-3xl font-black text-slate-900">
                {filteredJobs.length}
              </p>
              <p className="text-sm text-slate-500">
                Open Roles
              </p>
            </div>

            <div className="bg-white rounded-2xl px-6 py-4 shadow-sm border border-slate-100">
              <p className="text-3xl font-black text-emerald-600">
                100%
              </p>
              <p className="text-sm text-slate-500">
                Growth Support
              </p>
            </div>
          </div>
        </div>

        {/* ================= FILTER SECTION ================= */}
        <div className="bg-white rounded-3xl border border-slate-200 p-5 mb-8 shadow-sm">
          <div className="grid md:grid-cols-3 gap-4">

            {/* SEARCH */}
            <div className="relative">
              <Search
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                size={18}
              />

              <input
                type="text"
                placeholder="Search jobs..."
                value={searchTerm}
                onChange={(e) =>
                  setSearchTerm(e.target.value)
                }
                className="w-full border border-slate-200 rounded-2xl pl-12 pr-4 py-3 outline-none focus:border-indigo-500"
              />
            </div>

            {/* LOCATION */}
            <div className="relative">
              <MapPin
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                size={18}
              />

              <input
                type="text"
                placeholder="Filter by location"
                value={locationFilter}
                onChange={(e) =>
                  setLocationFilter(
                    e.target.value
                  )
                }
                className="w-full border border-slate-200 rounded-2xl pl-12 pr-4 py-3 outline-none focus:border-indigo-500"
              />
            </div>

            {/* WORK MODE */}
            <div className="relative">
              <Briefcase
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                size={18}
              />

              <select
                value={workModeFilter}
                onChange={(e) =>
                  setWorkModeFilter(
                    e.target.value
                  )
                }
                className="w-full border border-slate-200 rounded-2xl pl-12 pr-4 py-3 outline-none focus:border-indigo-500 bg-white"
              >
                <option value="">
                  All Work Modes
                </option>

                <option value="Remote">
                  Remote
                </option>

                <option value="Hybrid">
                  Hybrid
                </option>

                <option value="Onsite">
                  Onsite
                </option>
              </select>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">

          {/* ================= JOBS LIST ================= */}
          <div className="lg:col-span-8">
            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2
                  className="animate-spin text-indigo-600"
                  size={48}
                />
              </div>
            ) : (
              <div className="space-y-5">
                {filteredJobs.map((job) => (
                  <div
                    key={job.jobId}
                    className="bg-white border border-slate-200 rounded-3xl p-6 hover:shadow-xl hover:border-indigo-200 transition-all duration-300"
                  >
                    <div className="flex flex-col md:flex-row gap-5">

                      {/* ICON */}
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center flex-shrink-0">
                        <Building2
                          className="text-white"
                          size={26}
                        />
                      </div>

                      {/* CONTENT */}
                      <div className="flex-1">
                        <div className="flex flex-wrap gap-2 mb-3">
                          <span className="bg-indigo-100 text-indigo-700 text-xs font-bold px-3 py-1 rounded-full">
                            {job.jobType}
                          </span>

                          <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-3 py-1 rounded-full">
                            {job.workMode}
                          </span>
                        </div>

                        <h3 className="text-xl font-bold text-slate-900 hover:text-indigo-600 transition">
                          {job.jobTitle}
                        </h3>

                        <p className="text-slate-600 mt-2 line-clamp-2 text-sm">
                          {job.jobDescription ||
                            "Exciting opportunity to grow with modern technologies."}
                        </p>

                        <div className="flex flex-wrap gap-4 mt-4 text-sm">
                          <div className="flex items-center gap-2 text-slate-600">
                            <MapPin size={16} />{" "}
                            {job.location}
                          </div>

                          <div className="flex items-center gap-2 text-slate-600">
                            <Clock size={16} />{" "}
                            {
                              job.experienceLevel
                            }{" "}
                            yrs
                          </div>

                          <div className="flex items-center gap-2 text-slate-600">
                            <IndianRupee
                              size={16}
                            />{" "}
                            {job.salaryRange}
                          </div>
                        </div>
                      </div>

                      {/* ACTIONS */}
                      <div className="flex flex-col gap-3 md:w-44 pt-2">
                        <button
                          onClick={() =>
                            handleViewDetails(job)
                          }
                          className="px-5 py-3 text-sm font-semibold border border-slate-300 rounded-2xl hover:bg-slate-50 transition"
                        >
                          View Details
                        </button>

                        <button
                          onClick={() =>
                            handleApplyClick(job)
                          }
                          className="px-5 py-3 text-sm font-semibold bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-2xl hover:brightness-105 transition flex items-center justify-center gap-2"
                        >
                          Apply Now{" "}
                          <ArrowRight
                            size={16}
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {!loading &&
                  filteredJobs.length === 0 && (
                    <div className="bg-white rounded-3xl border border-slate-200 p-12 text-center">
                      <h3 className="text-2xl font-bold text-slate-800">
                        No Jobs Found
                      </h3>

                      <p className="text-slate-500 mt-2">
                        Try changing filters
                      </p>
                    </div>
                  )}
              </div>
            )}
          </div>

          {/* ================= SIDEBAR ================= */}
          <div className="lg:col-span-4 space-y-6">

            {/* WHY JOIN */}
            <div className="bg-white border border-slate-200 rounded-3xl p-6">
              <div className="flex items-center gap-3 mb-5">
                <Trophy
                  className="text-amber-500"
                  size={26}
                />

                <h3 className="font-bold text-xl text-slate-900">
                  Why Join Us?
                </h3>
              </div>

              <div className="space-y-4">
                {[
                  {
                    icon: (
                      <Users size={20} />
                    ),
                    text: "Collaborative & Supportive Culture",
                  },
                  {
                    icon: (
                      <Trophy size={20} />
                    ),
                    text: "Fast Career Growth",
                  },
                  {
                    icon: (
                      <Heart size={20} />
                    ),
                    text: "Work-Life Balance",
                  },
                  {
                    icon: (
                      <BadgeCheck size={20} />
                    ),
                    text: "Latest Tech Stack",
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3"
                  >
                    <div className="mt-0.5 text-indigo-600">
                      {item.icon}
                    </div>

                    <p className="text-slate-700 text-sm leading-relaxed">
                      {item.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ================= VIEW MODAL ================= */}
      {isViewModalOpen &&
        selectedJob && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-3xl rounded-3xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto">

              <div className="bg-gradient-to-r from-slate-900 to-indigo-900 text-white p-8 relative">
                <button
                  onClick={() =>
                    setIsViewModalOpen(false)
                  }
                  className="absolute top-6 right-6"
                >
                  <X size={22} />
                </button>

                <h2 className="text-3xl font-bold">
                  {selectedJob.jobTitle}
                </h2>

                <p className="text-indigo-200 mt-2">
                  {
                    selectedJob.department
                  }
                </p>
              </div>

              <div className="p-8 space-y-6">

                <div className="grid grid-cols-2 gap-4">
                  {[
                    {
                      label: "Location",
                      value:
                        selectedJob.location,
                    },
                    {
                      label: "Salary",
                      value:
                        selectedJob.salaryRange,
                    },
                    {
                      label: "Experience",
                      value: `${selectedJob.experienceLevel} Years`,
                    },
                    {
                      label: "Work Mode",
                      value:
                        selectedJob.workMode,
                    },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="bg-slate-50 rounded-2xl p-4"
                    >
                      <p className="text-xs text-slate-500">
                        {item.label}
                      </p>

                      <p className="font-semibold text-slate-800 mt-1">
                        {item.value}
                      </p>
                    </div>
                  ))}
                </div>

                <div>
                  <h4 className="font-bold mb-3">
                    Job Description
                  </h4>

                  <p className="text-slate-600 leading-relaxed">
                    {
                      selectedJob.jobDescription
                    }
                  </p>
                </div>

                <button
                  onClick={() => {
                    setIsViewModalOpen(
                      false
                    );

                    handleApplyClick(
                      selectedJob
                    );
                  }}
                  className="w-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white py-4 rounded-2xl font-bold flex items-center justify-center gap-2"
                >
                  Apply For This Position{" "}
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </div>
        )}

      {/* ================= APPLY MODAL ================= */}
      {isApplyModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">

          <div className="bg-white w-full max-w-4xl rounded-3xl overflow-hidden shadow-2xl max-h-[92vh] overflow-y-auto">

            <div className="sticky top-0 bg-white border-b px-8 py-5 flex justify-between items-center z-10">
              <div>
                <h2 className="text-2xl font-bold">
                  Apply Now
                </h2>

                <p className="text-sm text-slate-500">
                  {selectedJob?.jobTitle}
                </p>
              </div>

              <button
                onClick={() =>
                  setIsApplyModalOpen(false)
                }
              >
                <X size={24} />
              </button>
            </div>

            <form
              onSubmit={
                handleSubmitApplication
              }
              className="p-8 grid md:grid-cols-2 gap-6"
            >
              {[
                [
                  "Full Name",
                  "fullName",
                ],
                [
                  "Email",
                  "emailAddress",
                ],
                [
                  "Mobile",
                  "mobileNumber",
                ],
                [
                  "Current Location",
                  "currentLocation",
                ],
                [
                  "Experience (Years)",
                  "totalExperience",
                ],
                [
                  "Current Company",
                  "currentCompany",
                ],
                [
                  "Current Salary",
                  "currentSalary",
                ],
                [
                  "Expected Salary",
                  "expectedSalary",
                ],
                [
                  "Notice Period",
                  "noticePeriod",
                ],
                [
                  "Skills",
                  "skillsTechnologies",
                ],
                [
                  "Languages Known",
                  "languagesKnown",
                ],
                [
                  "LinkedIn",
                  "linkedInProfile",
                ],
                [
                  "GitHub / Portfolio",
                  "gitHubPortfolioLink",
                ],
              ].map(([label, name]) => (
                <div key={name}>
                  <label className="text-sm font-medium text-slate-700 block mb-1.5">
                    {label}
                  </label>

                  <input
                    type="text"
                    name={name}
                    value={formData[name]}
                    onChange={
                      handleChange
                    }
                    className="w-full border border-slate-200 rounded-2xl px-5 py-3 focus:border-indigo-500 focus:ring-1 outline-none"
                    placeholder={`Enter ${label}`}
                  />
                </div>
              ))}

              {/* RESUME FILE */}
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-slate-700 block mb-1.5">
                  Upload Resume
                </label>

                <div className="relative">
                  <Upload
                    className="absolute left-5 top-4 text-slate-400"
                    size={18}
                  />

                  <input
                    type="file"
                    name="resumeFile"
                    onChange={
                      handleChange
                    }
                    accept=".pdf,.doc,.docx"
                    className="w-full border border-slate-200 rounded-2xl pl-12 py-3 pr-4 focus:border-indigo-500 outline-none"
                  />
                </div>

                {formData.resumeFile && (
                  <p className="text-sm text-emerald-600 mt-2 font-medium">
                    Selected File:{" "}
                    {
                      formData.resumeFile
                    }
                  </p>
                )}
              </div>

              {/* JOINING DATE */}
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1.5">
                  Available Joining Date
                </label>

                <input
                  type="date"
                  name="availableJoiningDate"
                  value={
                    formData.availableJoiningDate
                  }
                  onChange={
                    handleChange
                  }
                  className="w-full border border-slate-200 rounded-2xl px-5 py-3 focus:border-indigo-500 focus:ring-1 outline-none"
                />
              </div>

              {/* SUBMIT */}
              <div className="md:col-span-2">
                <button
                  type="submit"
                  disabled={applyLoading}
                  className="w-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white py-4 rounded-2xl font-bold flex items-center justify-center gap-2 disabled:opacity-70"
                >
                  {applyLoading ? (
                    <Loader2
                      className="animate-spin"
                      size={20}
                    />
                  ) : (
                    <Send size={20} />
                  )}

                  {applyLoading
                    ? "Submitting..."
                    : "Submit Application"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Career;